import { FC, PropsWithChildren, ReactNode, useEffect } from 'react';
import { State } from '@weave/schema-gen-ts/dist/schemas/platform-location-feature/v1/platform-enums/location_feature_enum.pb';
import { Feature } from '@weave/schema-gen-ts/dist/shared/feature/location_feature.pb';
import { useTranslation } from '@frontend/i18n';
import { ContentLoader, useModalControl } from '@frontend/design-system';
import { useFeaturePromotionModal } from '../hooks/useFeaturePromotionModal';
import { useFeaturePromotionPage } from '../hooks/useFeaturePromotionPage';
import { useFeatureSubscriptionConfig } from '../hooks/useFeatureSubscriptionConfig';
import { useFeatureUpgradeDetailsModal } from '../hooks/useFeatureUpgradeDetailsModal';
import { FeatureSubscriptionContext } from '../providers/FeatureSubscriptionProvider';
import { FeatureDemoModal } from './feature-demo-modal/FeatureDemoModal';
import { FeatureMultiLocationUpgradeAlert } from './FeatureMultiLocationUpgradeAlert';

type FeatureSubscriptionGuardProps = {
  feature: Feature;
  locationIds?: string[];
  demoModalContent?: ReactNode;
  showMultiSubscriptionAlert?: boolean;
  onDemoModalOpen?: () => void;
  onDemoModalClose?: () => void;
} & (
  | {
      allowPromoteState?: boolean;
      activeStates?: never;
    }
  | {
      allowPromoteState?: never;
      activeStates?: State[];
    }
);

const STANDARD_ACTIVE_STATES = [State.ACTIVE];
const EXTENDED_ACTIVE_STATES = [State.ACTIVE, State.PROMOTE];

export const FeatureSubscriptionGuard: FC<PropsWithChildren<FeatureSubscriptionGuardProps>> = ({
  children,
  demoModalContent,
  feature,
  showMultiSubscriptionAlert = true,
  locationIds,
  onDemoModalOpen,
  onDemoModalClose,
  allowPromoteState,
  activeStates = STANDARD_ACTIVE_STATES,
}) => {
  const featureSubscriptionConfig = useFeatureSubscriptionConfig({
    feature,
    locationIds,
    activeStates: allowPromoteState ? EXTENDED_ACTIVE_STATES : activeStates,
  });
  const { isBundleUpgradeEligible, isFeatureActiveInAllLocations, isFeatureHiddenInAllLocations, isLoading } =
    featureSubscriptionConfig;
  const { featurePromotionModalControls, FeaturePromotionModal } = useFeaturePromotionModal();
  const { FeatureUpgradeDetailsModal, featureUpgradeDetailsModalControls } = useFeatureUpgradeDetailsModal({
    feature,
    isBundleUpgradeEligible,
  });
  const { FeaturePromotionPage } = useFeaturePromotionPage(feature);
  const demoModalControls = useModalControl();

  const { t } = useTranslation('featurePromotion', { keyPrefix: 'feature-subscription-guard' });

  useEffect(() => {
    // Close all modals if unmounted
    return () => {
      featurePromotionModalControls.closeModal();
      featureUpgradeDetailsModalControls.closeModal();
      demoModalControls.closeModal();
    };
  }, []);

  return (
    <FeatureSubscriptionContext.Provider value={featureSubscriptionConfig}>
      <ContentLoader show={isLoading} message={t('Checking Subscription')} />
      {/* Show children if feature is Active in some of the location */}
      {!isFeatureHiddenInAllLocations && !isLoading && children}
      {/* Show teaser if feature is hidden in all of the location and promotion page is available*/}
      {isFeatureHiddenInAllLocations && !!FeaturePromotionPage && (
        <FeaturePromotionPage
          mode='fullPage'
          handleOpenUpgradeModal={featureUpgradeDetailsModalControls.openModal}
          isDemoAvailable={!!demoModalContent}
          handleViewDemo={() => {
            demoModalControls.openModal();
            onDemoModalOpen?.();
          }}
        />
      )}
      {/* Show multi location alert if feature is active in some of the locations */}
      {showMultiSubscriptionAlert && !isFeatureActiveInAllLocations && !isFeatureHiddenInAllLocations && (
        <FeatureMultiLocationUpgradeAlert
          feature={feature}
          onUpgradeActionClick={
            !!FeatureUpgradeDetailsModal
              ? featureUpgradeDetailsModalControls.openModal
              : featurePromotionModalControls.openModal
          }
        />
      )}
      <FeaturePromotionModal
        feature={feature}
        modalControls={featurePromotionModalControls}
        handleOpenUpgradeModal={featureUpgradeDetailsModalControls.openModal}
      />
      {!!FeatureUpgradeDetailsModal && (
        <FeatureUpgradeDetailsModal
          modalControls={featureUpgradeDetailsModalControls}
          onActionSuccess={() => {
            featureUpgradeDetailsModalControls.closeModal();
          }}
        />
      )}
      {!!demoModalContent && (
        <FeatureDemoModal
          onUpgradeAction={featureUpgradeDetailsModalControls.openModal}
          {...demoModalControls.modalProps}
          onClose={() => {
            onDemoModalClose?.();
            demoModalControls.closeModal();
          }}
        >
          {demoModalContent}
        </FeatureDemoModal>
      )}
    </FeatureSubscriptionContext.Provider>
  );
};
